import * as React from 'react'
import { CaretLeft, CaretRight } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'
import { outlineSecondarySurface } from './button'

/**
 * Pagination — navigate page-split content (tables, search results, listings).
 * Renders a `<nav aria-label="Pagination">` with Previous/Next controls around
 * numbered page buttons; the active page carries `aria-current="page"`. Long
 * ranges collapse to first/last + a window around the current page with ellipses
 * (tune the window with `siblingCount`).
 *
 *   <Pagination currentPage={page} totalPages={10} onPageChange={setPage} />
 *   <Pagination … variant="compact" size="sm" sideLayout="icon" />
 *
 * `size` — `sm` (32px) | `md` (40px, default), matching the DS2026 table footer.
 * `tone` — `subtle` (default; the DS2026 neutral-gray active page, no brand fill)
 * or `brand` (active page filled with brand green). `variant` — `default`
 * (numbered) or `compact` ("Page X of Y" between the controls). Previous/Next are
 * DS2026 secondary (outline) buttons (the page numbers are ghost); `sideLayout`
 * shapes them: `full` (icon + label), `label` (label on ≥sm, icon on mobile), or
 * `icon` (icon only). Built on plain buttons + Phosphor carets.
 */

export interface PaginationProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> {
  /** Active page, 1-based. */
  currentPage: number
  /** Total number of pages. */
  totalPages: number
  /** Called with the next page when a control is activated. */
  onPageChange: (page: number) => void
  variant?: 'default' | 'compact'
  sideLayout?: 'full' | 'label' | 'icon'
  size?: 'sm' | 'md'
  /** Active-page styling: `subtle` (DS2026 neutral, default) or `brand` (green fill). */
  tone?: 'subtle' | 'brand'
  /** Page numbers to show on each side of the current page (default 1). */
  siblingCount?: number
}

const sizeCfg = {
  // DS2026 table-footer sizes: 32px (sm) / 40px (md), radius 6/8, 12/14px text.
  sm: {
    control: 'h-8 min-w-8 rounded-md px-2 text-xs',
    label: 'px-1',
    icon: 'size-4',
  },
  md: {
    control: 'h-10 min-w-10 rounded-lg px-2.5 text-sm',
    label: 'px-1',
    icon: 'size-4',
  },
} as const

const controlBase =
  'inline-flex items-center justify-center gap-1 font-medium tracking-[-0.2px] tabular-nums outline-none transition-colors focus-visible:ring-2 focus-visible:ring-fbui-ring-brand disabled:pointer-events-none disabled:opacity-40'

// `subtle` is the DS2026 table-footer look (neutral gray active, no brand fill);
// `brand` fills the active page with brand green. Side controls always use the
// tone's inactive style.
const toneCfg = {
  subtle: {
    inactive: 'text-fbui-gray-700 hover:bg-fbui-gray-100 hover:text-fbui-gray-900',
    active: 'bg-fbui-gray-100 font-semibold text-fbui-gray-900',
  },
  brand: {
    inactive: 'text-fbui-gray-700 hover:bg-fbui-gray-100',
    active: 'bg-fbui-basil-600 text-fbui-base-white hover:bg-fbui-basil-700',
  },
} as const

// Previous/Next are DS2026 secondary (outline) buttons — the same neutral bordered
// surface as `Button` `appearance="outline"` (shared below) — not ghosts like the
// page numbers. Focus ring + disabled come from `controlBase`.
const secondaryControl = outlineSecondarySurface

const range = (start: number, end: number): number[] =>
  Array.from({ length: end - start + 1 }, (_, i) => start + i)

/** Page items to render: page numbers interleaved with `'dots'` gaps. */
const getPageItems = (
  current: number,
  total: number,
  siblingCount: number
): (number | 'dots')[] => {
  // first + last + current + 2*siblings + 2 dot slots
  const totalSlots = siblingCount * 2 + 5
  if (total <= totalSlots) return range(1, total)

  const leftSibling = Math.max(current - siblingCount, 1)
  const rightSibling = Math.min(current + siblingCount, total)
  const showLeftDots = leftSibling > 2
  const showRightDots = rightSibling < total - 1

  if (!showLeftDots && showRightDots) {
    return [...range(1, 3 + siblingCount * 2), 'dots', total]
  }
  if (showLeftDots && !showRightDots) {
    return [1, 'dots', ...range(total - (2 + siblingCount * 2), total)]
  }
  return [1, 'dots', ...range(leftSibling, rightSibling), 'dots', total]
}

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  variant = 'default',
  sideLayout = 'full',
  size = 'md',
  tone = 'subtle',
  siblingCount = 1,
  className,
  'aria-label': ariaLabel = 'Pagination',
  ...props
}: PaginationProps) => {
  const cfg = sizeCfg[size]
  const tones = toneCfg[tone]
  const atStart = currentPage <= 1
  const atEnd = currentPage >= totalPages

  const sideButton = (direction: 'prev' | 'next') => {
    const isPrev = direction === 'prev'
    const Icon = isPrev ? CaretLeft : CaretRight
    const label = isPrev ? 'Previous' : 'Next'
    const icon = <Icon weight="bold" aria-hidden="true" className={cfg.icon} />
    let content: React.ReactNode
    if (sideLayout === 'icon') {
      content = icon
    } else if (sideLayout === 'label') {
      // Label on ≥sm, icon on mobile to save space.
      content = (
        <>
          <span className="hidden sm:inline">{label}</span>
          <span className={cn('sm:hidden', cfg.label)}>{icon}</span>
        </>
      )
    } else {
      content = isPrev ? (
        <>
          {icon}
          <span className={cfg.label}>{label}</span>
        </>
      ) : (
        <>
          <span className={cfg.label}>{label}</span>
          {icon}
        </>
      )
    }
    return (
      <button
        type="button"
        aria-label={`${label} page`}
        disabled={isPrev ? atStart : atEnd}
        onClick={() => onPageChange(currentPage + (isPrev ? -1 : 1))}
        className={cn(controlBase, cfg.control, secondaryControl)}
      >
        {content}
      </button>
    )
  }

  return (
    <nav
      aria-label={ariaLabel}
      className={cn('flex items-center gap-1', className)}
      {...props}
    >
      {sideButton('prev')}

      {variant === 'compact' ? (
        <span className="px-3 text-sm tabular-nums text-fbui-gray-700">
          Page {currentPage} of {totalPages}
        </span>
      ) : (
        getPageItems(currentPage, totalPages, siblingCount).map((item, i) =>
          item === 'dots' ? (
            <span
              key={`dots-${i}`}
              aria-hidden="true"
              className={cn(
                'inline-flex items-center justify-center text-fbui-gray-500',
                cfg.control
              )}
            >
              …
            </span>
          ) : (
            <button
              key={item}
              type="button"
              aria-label={`Page ${item}`}
              aria-current={item === currentPage ? 'page' : undefined}
              onClick={() => onPageChange(item)}
              className={cn(
                controlBase,
                cfg.control,
                item === currentPage ? tones.active : tones.inactive
              )}
            >
              {item}
            </button>
          )
        )
      )}

      {sideButton('next')}
    </nav>
  )
}
Pagination.displayName = 'Pagination'
